import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PrivateRouter from './PrivateRouter';
import PublicRouter from './PublicRouter';
import { Loading } from '../Loanding';
import { useIdContext } from '../context/VariableContext';
import { ErrorBoundary } from './ErrorBoundary';
import { NavigationLogger } from './useNavigationLogger';

const QuizDiagnosticV2 = React.lazy(() => import('../pages/v2/QuizDiagnosticV2'));
const ReservaCoworkPage = React.lazy(() => import('../pages/v2/reservations/ReservaCoworkPage'));
// Registro de datos en el stand People Day 2026 (sin reserva): pública, con o sin sesión.
const RegistroEventoPage = React.lazy(() => import('../pages/v2/reservations/RegistroEventoPage'));

const QUIZ_REST_REGEX = /^\/quiz\/rest(\/[^/]+)?\/?$/;
// S2-133 — reserva cowork People Day Colombia 2026 (BUK): pública, con o sin sesión.
const RESERVA_COWORK_REGEX = /^\/participantes\/reserva-cowork\/?$/;
const REGISTRO_EVENTO_REGEX = /^\/participantes\/registro-evento\/?$/;

/**
 * Redirige /quiz/rest/:a/:b... (multi-segmento) a /quiz/rest.
 * Se ejecuta DENTRO del BrowserRouter para respetar history.
 */
const QuizRestRedirect = () => {
    const location = useLocation();
    const isMultiSegment = /^\/quiz\/rest\/[^/]+\/.+/.test(location.pathname);

    if (isMultiSegment) {
        return <Navigate to="/quiz/rest" replace />;
    }
    return null;
};

/**
 * Enruta /quiz/rest y /quiz/rest/:email fuera de la bifurcación público/privado
 * para que funcionen autenticado o no. El propio componente valida vía backend
 * si el email tiene una encuesta asignada y, si el usuario está logueado,
 * fuerza su email de sesión (no permite escribir otros).
 */
const BifurcatedRouter = ({ isLogged }) => {
    const location = useLocation();
    const isQuizRest = QUIZ_REST_REGEX.test(location.pathname);

    if (isQuizRest) {
        return (
            <Suspense fallback={<Loading />}>
                <Routes>
                    <Route path="/quiz/rest" element={<QuizDiagnosticV2 />} />
                    <Route path="/quiz/rest/:email" element={<QuizDiagnosticV2 />} />
                </Routes>
            </Suspense>
        );
    }

    if (RESERVA_COWORK_REGEX.test(location.pathname)) {
        return (
            <Suspense fallback={<Loading />}>
                <Routes>
                    <Route path="/participantes/reserva-cowork" element={<ReservaCoworkPage />} />
                </Routes>
            </Suspense>
        );
    }

    if (REGISTRO_EVENTO_REGEX.test(location.pathname)) {
        return (
            <Suspense fallback={<Loading />}>
                <Routes>
                    <Route path="/participantes/registro-evento" element={<RegistroEventoPage />} />
                </Routes>
            </Suspense>
        );
    }

    return isLogged ? <PrivateRouter /> : <PublicRouter />;
};

const AppRouter = () => {
    const { user, authResolved } = useAuth();
    const { load } = useIdContext();
    const [minSplash, setMinSplash] = useState(true);

    // Splash mínimo de 300ms para evitar flicker en reloads rápidos.
    useEffect(() => {
        const t = setTimeout(() => setMinSplash(false), 300);
        return () => clearTimeout(t);
    }, []);

    // Esperamos a que:
    //  1. Firebase Auth resuelva si hay sesión (authResolved === true)
    //     → Evita mostrar 404 al recargar una ruta privada
    //  2. Los contextos iniciales terminen de cargar (load === false)
    //  3. El splash mínimo haya pasado
    const isLoading = !authResolved || load || minSplash;

    if (isLoading) return <Loading />;

    // Guard explícito: solo es "logueado" si user existe Y logged === true
    const isLogged = Boolean(user && user.logged === true);

    return (
        <ErrorBoundary>
            <BrowserRouter>
                <NavigationLogger />
                <QuizRestRedirect />
                <BifurcatedRouter isLogged={isLogged} />
            </BrowserRouter>
        </ErrorBoundary>
    );
};

export default AppRouter;
